import React from "react";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import type {Theme} from '@mui/material/styles';
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import NextLink from "next/link";
import {CmLinkableWithChildren} from "../types/navigation";
import {commonStyles} from "../styles";
import {CmLinkableType} from "../../../../types";
import {useNavigationTracking} from "tracking/hooks/useNavigationTracking";

export interface MobileMenuItemProps {
    item: CmLinkableType;
    level: 0 | 1 | 2;
    index?: number;
    totalItems?: number;
    isActive: boolean;
    onClick: (item: CmLinkableType) => void;
    onClose: () => void;
    isEasyLangMode?: boolean;
}


const MobileMenuItem: React.FC<MobileMenuItemProps> = ({
                                                           item,
                                                           level,
                                                           index = 0,
                                                           totalItems = 0,
                                                           isActive,
                                                           onClick,
                                                           onClose,
                                                           isEasyLangMode
                                                       }) => {
    const {trackMainNavigation} = useNavigationTracking();
    const itemWithChildren = item as CmLinkableWithChildren;
    const hasChildren = !!itemWithChildren.children && itemWithChildren.children.length > 0;
    const isFirstLevel = level === 0;
    const isLastLevel = level === 2;
    const canExpand = hasChildren && !isLastLevel;

    function handleMobileMenuItemClick() {
        if (canExpand) {
            onClick(item);
            return;
        }
        trackMainNavigation(level, itemWithChildren.teaserTitle, {isMobile: true});
        onClose();
    }

    // Determine height based on level
    const height = isFirstLevel ? "56px" : "44px";

    // Determine border styles based on level and position
    let borderStyles;
    if (isFirstLevel) {
        borderStyles = {borderBottom: (theme: Theme) => `1px solid ${theme.palette.divider}`};
    } else {
        borderStyles = {
            borderTop: () => "none",
            borderBottom: (theme: Theme) => index === totalItems - 1 ?
                `1px solid ${theme.palette.divider}` : "none"
        };
    }

    const fontSize = !isFirstLevel ? '14px' : undefined;

    const componentProp = canExpand ? "div" : NextLink;
    const hrefProp = canExpand ? undefined : (item.selfUrl || "#");

    return (
        <ListItemButton
            data-cypress={`mobileMenuItem-${level}-${index}`}
            aria-expanded={canExpand ? false : undefined}
            onClick={handleMobileMenuItemClick}
            component={componentProp}
            href={hrefProp}
            sx={{
                height,
                ...commonStyles.menuItemLayout,
                ...borderStyles,
                "&:hover": commonStyles.hoverStyles,
                ...commonStyles.activeStyles(isActive, isFirstLevel, hasChildren, isEasyLangMode)
            }}
        >
            <ListItemText
                primary={itemWithChildren.teaserTitle}
                slotProps={{
                    primary: commonStyles.menuItemTypographyProps(fontSize)
                }}
            />
            {canExpand && (
                <ChevronRightIcon color="action"/>
            )}
        </ListItemButton>
    );
};

export default MobileMenuItem;
